import { useEffect, useRef } from 'react';
import logger from 'ws-scripts/modules/logger';
import { useFetchLocationGeoZip } from './useFetchLocationGeoZip';
import { useLocationsData } from './useLocationsData';

const LOG_PREFIX = '[ws-locations-control][useLocationSearch]';
const DEFAULT_SEARCH_RANGE = 50;
const SESSION_STORAGE_RADIUS_KEY = 'locationSearchRange';
const EXCLUDE_BY_RANGE = true;

/**
 * Get the current search range, preferring the user's persisted preference.
 * Falls back to default if no preference is stored or the value is invalid.
 */
const getCurrentRange = () => {
	try {
		const stored = sessionStorage.getItem(SESSION_STORAGE_RADIUS_KEY);
		if (stored) {
			const parsed = parseInt(stored, 10);
			if (!Number.isNaN(parsed) && parsed > 0) {
				return parsed;
			}
		}
	} catch (e) {
		// sessionStorage unavailable
	}
	return DEFAULT_SEARCH_RANGE;
};

/**
 * Hook that orchestrates geo-zip fetching and location search.
 * Responsibilities:
 * - Fetches geo-zip from a place ID via useFetchLocationGeoZip
 * - Manages location data lifecycle via useLocationsData
 * - Triggers search params updates when data is ready
 *
 * @param {Object} params
 * @param {boolean} params.isApiReady - Whether the Google Maps API is ready.
 * @param {object|null} params.sessionToken - The active AutocompleteSessionToken.
 * @param {Function} params.resetSession - Function to reset/end the session.
 * @param {string|null} params.selectedPlaceId - The selected Google Place ID.
 * @returns {{ locationCount: number, geoError: string|null, locationError: string|null, isLoading: boolean }}
 */
export const useLocationSearch = ({
	isApiReady,
	sessionToken,
	resetSession,
	selectedPlaceId
}) => {
	// Fetch geo-zip from place ID
	const {
		zipcode: geoZip,
		isLoading: geoLoading,
		error: geoError
	} = useFetchLocationGeoZip(
		isApiReady,
		sessionToken,
		resetSession,
		selectedPlaceId
	);

	// Manage location data
	const {
		locationCount,
		error: locationError,
		isLoading: locationLoading,
		isReady: isLocationsDataReady,
		setSearchParams
	} = useLocationsData();

	logger.debug(`${LOG_PREFIX} State`, {
		isReady: isLocationsDataReady,
		count: locationCount,
		geoZip
	});

	// Update search params only for a user-initiated search.
	// Initial load is owned by ws-locations-data, so we never trigger until
	// the user resolves a geoZip. Once that happens, a subsequent null geoZip
	// means the user cleared their place — reset to the default search.
	const hasUserSearchedRef = useRef(false);
	useEffect(() => {
		if (!isLocationsDataReady || geoLoading) {
			return;
		}

		if (geoZip !== null) {
			hasUserSearchedRef.current = true;
		} else if (!hasUserSearchedRef.current) {
			return;
		}

		setSearchParams({
			postalCode: geoZip,
			range: getCurrentRange(),
			excludeByRange: EXCLUDE_BY_RANGE,
			useDefaultTileCount: geoZip === null
		});
	}, [isLocationsDataReady, geoZip, setSearchParams, geoLoading]);

	const isLoading = geoLoading || locationLoading;

	return {
		locationCount,
		geoError,
		locationError,
		isLoading
	};
};
